import React, { useEffect, useState, useRef } from 'react';
import { submitPriceRequest } from '../services/api';
import { companyInfo, contactData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, TreePine } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [selectedWorks, setSelectedWorks] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);


  useEffect(() => {
    const handlePrefill = (event) => {
      const services = event.detail?.services || [];
      const servicesLabel = t('contact.prefillLabel', 'Vēlos saņemt cenu piedāvājumu par');
      setSelectedWorks(services);
      if (services.length) {
        setFormData((prev) => ({
          ...prev,
          message: `${servicesLabel}: ${services.join(', ')}.`,
        }));
        setIsSubmitted(false);
      }
    };

    window.addEventListener('prefill-contact', handlePrefill);
    return () => window.removeEventListener('prefill-contact', handlePrefill);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await submitPriceRequest({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        services: selectedWorks,
        source: selectedWorks.length ? 'price_request' : 'contact'
      });

      setIsSubmitted(true);
      if (response?.email_status === 'failed') {
        toast.warning(t('contact.toastSavedTitle', 'Pieteikums saglabāts'), {
          description: t('contact.toastSavedBody', 'E‑pasta piegādei nepieciešams verificēts domēns. Ziņojums ir redzams admin portālā.')
        });
      } else {
        toast.success(t('contact.toastSentTitle', 'Pieteikums nosūtīts!'), {
          description: t('contact.toastSentBody', 'Mēs ar Jums sazināsimies tuvākajā laikā.'),
        });
      }

      setTimeout(() => {
        setFormData({ name: '', phone: '', email: '', message: '' });
        setSelectedWorks([]);
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      const detail = error.response?.data?.detail || t('contact.submitError', 'Kļūda nosūtot pieteikumu. Lūdzu mēģiniet vēlreiz.');
      setSubmitError(detail);
      toast.error(t('contact.toastErrorTitle', 'Kļūda nosūtot pieteikumu'), {
        description: detail
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakti" className="py-24 section-glade relative overflow-hidden" data-testid="contact-section" ref={sectionRef}>
      <div className="absolute inset-0 forest-pattern opacity-20" />
      <div className="absolute inset-0 forest-grain opacity-40" />
      <div className="absolute top-12 right-12 w-72 h-72 bg-[#1F3D2B]/8 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-12 left-12 w-72 h-72 bg-[#1F3D2B]/6 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2.2s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className={`text-center sm:text-left mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-testid="contact-header" style={{ transitionDelay: '0.1s' }}>
          <span className="inline-block text-[#1F3D2B] font-medium text-sm tracking-wider uppercase mb-4" data-testid="contact-eyebrow">
            {t('contact.eyebrow', 'Sazinieties ar mums')}
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-4" data-testid="contact-title">
            {t('contact.title', contactData.title)}
          </h2>
          <p className="text-[#2E2E2E]/70 text-lg max-w-2xl mx-auto sm:mx-0" data-testid="contact-description">
            {t('contact.description', contactData.description)}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className={`lg:col-span-2 space-y-6 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: "0.2s" }}>
            <div className="forest-panel rounded-2xl p-6 shadow-sm hover-lift transition-all relative overflow-hidden" data-testid="contact-info-card">
              <img
                src={companyInfo.logo.transparent}
                alt={companyInfo.name}
                className="absolute -right-6 -bottom-10 w-56 opacity-10 pointer-events-none select-none"
                data-testid="contact-logo-watermark"
              />
              <h3 className="font-semibold text-[#2E2E2E] text-lg mb-6">
                {t('contact.infoTitle', 'Kontaktinformācija')}
              </h3>

              <div className="space-y-5">
                <div className="flex items-start gap-4" data-testid="contact-specialist-info">
                  <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0">
                    <TreePine className="w-5 h-5 text-[#1F3D2B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60 mb-1" data-testid="contact-specialist-name">
                      {t('contact.specialistName', 'Rūdolfs Mahts')}
                    </p>
                    <p className="font-semibold text-[#2E2E2E]" data-testid="contact-specialist-role">
                      {t('contact.specialistRole', 'Mežsaimniecības speciālists')}
                    </p>
                  </div>
                </div>
                <a
                  href={`tel:${companyInfo.phone}`} data-testid="contact-phone-link"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1F3D2B] transition-colors">
                    <Phone className="w-5 h-5 text-[#1F3D2B] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60 mb-1">{t('contact.phoneLabel', 'Tālrunis')}</p>
                    <p className="font-semibold text-[#2E2E2E] group-hover:text-[#1F3D2B] transition-colors">
                      {companyInfo.phone}
                    </p>
                  </div>
                </a>

                <a
                  href={`mailto:${companyInfo.email}`} data-testid="contact-email-link"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1F3D2B] transition-colors">
                    <Mail className="w-5 h-5 text-[#1F3D2B] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60 mb-1">{t('contact.emailLabel', 'E-pasts')}</p>
                    <p className="font-semibold text-[#2E2E2E] group-hover:text-[#1F3D2B] transition-colors">
                      {companyInfo.email}
                    </p>
                  </div>
                </a>

                <div className="flex items-start gap-4" data-testid="contact-address-info">
                  <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#1F3D2B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60 mb-1">{t('contact.addressLabel', 'Juridiskā adrese')}</p>
                    <p className="font-semibold text-[#2E2E2E] text-sm">
                      {companyInfo.address}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-hours-info">
                  <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-[#1F3D2B]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#2E2E2E]/60 mb-1">{t('contact.hoursLabel', 'Darba laiks')}</p>
                    <p className="font-semibold text-[#2E2E2E]">
                      {companyInfo.workingHours}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust note */}
            <div className="forest-panel-dark rounded-2xl p-6 text-white hover-lift transition-all">
              <h3 className="font-semibold text-lg mb-2">{t('contact.freeConsultationTitle', 'Bezmaksas konsultācija')}</h3>
              <p className="text-white/70 text-sm">
                {t('contact.freeConsultationText', 'Pirmā saruna un situācijas izvērtējums ir bez maksas. Sazinieties ar mums un kopīgi izlemsim labāko risinājumu.')}
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`lg:col-span-3 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: "0.3s" }}>
            <div className="forest-panel rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="contact-form-card">
              <h3 className="font-semibold text-[#2E2E2E] text-lg mb-2" data-testid="contact-form-title">
                {t('contact.formTitle', 'Pieteikuma forma')}
              </h3>
              <p className="text-[#2E2E2E]/60 text-sm mb-6" data-testid="contact-form-note">
                {t('contact.formNote', contactData.formNote)}
              </p>

              {isSubmitted ? (
                <div className="text-center py-12" data-testid="contact-success-message">
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-[#2E2E2E] mb-2">
                    {t('contact.successTitle', 'Paldies par pieteikumu!')}
                  </h4>
                  <p className="text-[#2E2E2E]/60">
                    {t('contact.successBody', 'Mēs ar Jums sazināsimies tuvākajā laikā.')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('contact.formNameLabel', 'Vārds, uzvārds')} *</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.formNamePlaceholder', 'Jānis Bērziņš')}
                        required
                        className="h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('contact.formPhoneLabel', 'Tālruņa numurs')} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        data-testid="contact-phone-input"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contact.formPhonePlaceholder', '+371 20 123 456')}
                        required
                        className="h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('contact.formEmailLabel', 'E-pasta adrese')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('contact.formEmailPlaceholder', 'janis@example.com')}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contact.formMessageLabel', 'Ziņojums')} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-textarea"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.formMessagePlaceholder', 'Aprakstiet savu situāciju vai jautājumu par meža īpašumu...')}
                      rows={5}
                      required
                      className="resize-none"
                    />
                  </div>

                  <div className="text-xs text-[#2E2E2E]/50">
                    {t('contact.formConsent', 'Iesūtot formu, jūs piekrītat savu personas datu apstrādei saskaņā ar GDPR prasībām.')}
                  </div>

                  {submitError && (
                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="contact-submit-error">
                      {submitError}
                    </div>
                  )}

                  <Button
                    type="submit"
                    size="lg"
                    data-testid="contact-submit-button"
                    className="w-full sm:w-auto forest-cta"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        {t('contact.formSubmitting', 'Nosūta...')}
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        {t('contact.formSubmit', 'Nosūtīt pieteikumu')}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
